import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Loading, ErrorState, EmptyState } from "./ui";
import {
  listManualEntries, createManualEntry, updateManualEntry, removeManualEntry,
} from "../api";
import { acct, num } from "../lib/format";

/**
 * Manual accounting entries.
 *
 * Everything else on this dashboard is imported, and every import mode we have
 * rewrites the rows it covers — so an adjustment typed into `expenses` would be
 * erased the next time Deltek restated that month. These live in their own
 * table and are unioned into the feeds at read time, which is what makes them
 * survive.
 *
 * Two kinds:
 *   Expense           — behaves like any ledger line under the account given.
 *   Revenue write-off — subtracts from revenue, on its own line of the P&L.
 *
 * A partner is required: without one the entry has no column to land in.
 */

const BLANK = {
  kind: "expense", account: "", period: "", username: "",
  amount: "", project: "", description: "",
};

const periodKey = (p) => {
  const [m, y] = String(p || "").split("/");
  return `${y || ""}${(m || "").padStart(2, "0")}`;
};

export default function ManualEntries() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);   // id being edited, or null
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  /* Newest first by default, which is how the list is usually read; any column
     can take over. Period sorts by YYYYMM, never as the text "01/2024". */
  const [sort, setSort] = useState({ key: "period", dir: "desc" });

  const load = useCallback(() => {
    setLoading(true); setError("");
    listManualEntries().then(setData).catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const accounts = useMemo(
    () => (data?.accounts || []).filter((a) => a.name),
    [data]
  );
  const sortValue = (r, key) => {
    switch (key) {
      case "period":  return periodKey(r.period);
      case "kind":    return r.kind === "writeoff" ? "Revenue write-off" : "Expense";
      case "account": return (r.account_name || r.account || "").toLowerCase();
      case "partner": return (r.display_name || r.username || "").toLowerCase();
      case "description": return (r.description || "").toLowerCase();
      case "amount":  return num(r.amount);
      default:        return "";
    }
  };

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    const sign = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      const c = typeof x === "number" ? x - y : String(x).localeCompare(String(y));
      // A stable tiebreak on id, so equal keys do not shuffle between renders.
      return (c || a.id - b.id) * sign;
    });
    return list;
  }, [data, sort]);

  /* Clicking a column sorts by it; clicking the same one again reverses. */
  const by = (key) => () =>
    setSort((s0) => ({ key, dir: s0.key === key && s0.dir === "asc" ? "desc" : "asc" }));
  const Th = ({ k, children, className }) => (
    <th className={`me-th${className ? ` ${className}` : ""}`}
        onClick={by(k)} title="Sort by this column"
        aria-sort={sort.key === k ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      {children}
      <span className="me-sort">{sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
    </th>
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => { setForm(BLANK); setEditing(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setMsg("");
    try {
      const body = { ...form, amount: Number(form.amount) };
      if (editing) await updateManualEntry(editing, body);
      else await createManualEntry(body);
      setMsg(editing ? "Entry updated" : "Entry added");
      reset();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const edit = (r) => {
    setEditing(r.id);
    setForm({
      kind: r.kind, account: r.account, period: r.period, username: r.username || "",
      amount: String(num(r.amount)), project: r.project || "",
      description: r.description || "",
    });
    setMsg("");
  };

  const del = async (r) => {
    // Deleting is the only way to undo one of these, so it is worth a pause.
    if (!window.confirm(`Delete the ${r.period} entry for ${r.username}?`)) return;
    try {
      await removeManualEntry(r.id);
      if (editing === r.id) reset();
      load();
    } catch (err) { setError(err.message); }
  };

  if (loading) return <Loading label="Loading entries…" />;

  return (
    <div className="stack">
      <Card title={editing ? "Edit entry" : "New entry"}
            note="account, period, partner and amount — the rest is optional">
        <form className="me-form" onSubmit={submit}>
          <label>
            <span>Type</span>
            <select value={form.kind} onChange={set("kind")}>
              <option value="expense">Expense</option>
              <option value="writeoff">Revenue write-off</option>
            </select>
          </label>
          <label>
            <span>Account</span>
            <select value={form.account} onChange={set("account")} required>
              <option value="">Choose…</option>
              {accounts.map((a) => (
                <option key={a.account_number} value={a.account_number}>
                  {a.account_number} — {a.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Period</span>
            <input value={form.period} onChange={set("period")}
                   placeholder="MM/YYYY" pattern="(0[1-9]|1[0-2]|13)/[0-9]{4}" required />
          </label>
          <label>
            <span>Partner</span>
            <select value={form.username} onChange={set("username")} required>
              <option value="">Choose…</option>
              {(data?.partners || []).map((p) => (
                <option key={p.username} value={p.username}>
                  {p.display_name || p.username.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Amount</span>
            <input type="number" step="0.01" value={form.amount}
                   onChange={set("amount")} required />
          </label>
          <label>
            <span>Project</span>
            <input value={form.project} onChange={set("project")}
                   placeholder="optional project number" />
          </label>
          <label className="me-wide">
            <span>Description</span>
            <input value={form.description} onChange={set("description")}
                   placeholder="what this adjustment is for" />
          </label>
          <div className="me-actions">
            <button className="btn btn--primary" disabled={saving}>
              {editing ? "Save changes" : "Add entry"}
            </button>
            {editing && (
              <button type="button" className="btn" onClick={reset}>Cancel</button>
            )}
            {msg && <span className="muted">{msg}</span>}
          </div>
        </form>
        {form.kind === "writeoff" && (
          <p className="toolbar-note">
            A write-off is entered as a positive figure and subtracted from revenue.
          </p>
        )}
      </Card>

      {error && <ErrorState message={error} onRetry={load} />}

      <Card title="Entries" note={`${rows.length} held outside the ledger`} flush>
        {!rows.length ? (
          <EmptyState title="Nothing entered yet"
                      hint="Adjustments you add above appear here, and survive every import." />
        ) : (
          <div className="tbl-wrap tbl-wrap--flush">
            <table className="tbl tbl-zebra">
              <thead>
                <tr>
                  <Th k="period">Period</Th>
                  <Th k="kind">Type</Th>
                  <Th k="account">Account</Th>
                  <Th k="partner">Partner</Th>
                  <Th k="description">Description</Th>
                  <Th k="amount" className="num">Amount</Th>
                  <th style={{ width: 132 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={editing === r.id ? "is-editing" : ""}>
                    <td>{r.period}</td>
                    <td>{r.kind === "writeoff" ? "Revenue write-off" : "Expense"}</td>
                    <td>{r.account_name || r.account}</td>
                    <td>{r.display_name || (r.username || "").toUpperCase()}</td>
                    <td className="muted">{r.description || "—"}</td>
                    <td className={`num ${num(r.amount) < 0 ? "neg" : ""}`}>
                      {acct(num(r.amount))}
                    </td>
                    <td className="me-actions-cell">
                      <div className="me-rowbtns">
                        <button className="btn btn--sm" onClick={() => edit(r)}>Edit</button>
                        <button className="btn btn--sm btn--danger" onClick={() => del(r)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
